<template>
  <div class="h-screen d-flex align-center justify-center">
    <div class="pa-8 text-center md:max-w-500px">
      <v-progress-circular
        v-if="confirming"
        class="mb-4"
        size="64"
        width="2"
        indeterminate
        color="primary"
      />
      <v-icon v-else-if="!hasError" size="64" class="mb-4" color="success">
        check_circle
      </v-icon>
      <v-icon v-else size="64" class="mb-4" color="error"> error </v-icon>

      <div class="text-h6 mb-2">
        {{
          confirming
            ? "Processing Payment"
            : hasError
            ? "Payment Failed"
            : "Payment Confirmed"
        }}
      </div>
      <div class="text-body-2 opacity-70">
        {{
          confirming
            ? "Please wait while we confirm your payment..."
            : hasError
            ? "There was an issue confirming your payment. Redirecting..."
            : isStoreCreation
            ? "Setting up your workspace..."
            : "Updating your subscription..."
        }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted } from "vue";
import { useRoute } from "vue-router";

export default defineComponent({
  setup() {
    definePageMeta({
      layout: "blank",
    });

    const route = useRoute();
    const pageLoader = useLoading();
    const confirming = ref(true);
    const hasError = ref(false);

    const { confirmPlanSubscription } = usePlanSubscription();
    const { waitForWorkspaceReady, exited } = useWorkspaceSetup();
    const { setAuthContext, fetchAuthContext } = useAuth();

    const plan = String(route.query.plan ?? "");
    const workspace = String(route.query.workspace ?? "");
    const context = String(route.query.context ?? "plan-subscription");
    const setupIntentId = String(route.query.setup_intent ?? "");
    const redirectStatus = String(route.query.redirect_status ?? "");

    const isStoreCreation = computed(() => context === "store-creation");

    const waitForWorkspaceSetup = (workspaceCode: string) => {
      pageLoader.loading(true, "Preparing your workspace");

      waitForWorkspaceReady(workspaceCode, {
        maxWaitTime: 1000 * 60 * 2, // 2 minutes
        navigate: true, // automatically switch to new workspace
      });

      // Show "Still preparing" after 5 seconds
      setTimeout(() => {
        if (!exited.value) {
          pageLoader.loading(true, "Still preparing workspace");
        }
      }, 5000);
    };

    const confirmPayment = () => {
      // Both store creation and plan subscription use the same mutation
      // Backend determines context from Stripe metadata (store ID, plan handle)
      return confirmPlanSubscription(plan, setupIntentId);
    };

    const handleSuccess = async (result: any) => {
      // Update auth context with new workspace/plan info
      if (result?.context) {
        setAuthContext(result.context);
      }

      // Refetch auth context to get updated workspace and plan details
      await fetchAuthContext().catch((error) => {
        console.error("Failed to refresh auth context:", error);
      });

      confirming.value = false;

      if (isStoreCreation.value && workspace) {
        // Wait for workspace to be ready
        waitForWorkspaceSetup(workspace);
      } else {
        // Plan subscription success - redirect to home
        setTimeout(() => {
          window.location.href = "/";
        }, 1500);
      }
    };

    const handleError = () => {
      confirming.value = false;
      hasError.value = true;
      // Redirect to home after error
      setTimeout(() => {
        window.location.href = "/";
      }, 3000);
    };

    onMounted(async () => {
      // If Stripe says the payment succeeded, confirm it
      if (redirectStatus === "succeeded" && plan) {
        try {
          const result = await confirmPayment();

          if (!result?.ok) {
            handleError();
            return;
          }

          await handleSuccess(result);
        } catch (err) {
          console.error("Payment confirmation failed:", err);
          handleError();
        }
      } else {
        // Redirect immediately if no valid payment status
        window.location.href = "/";
      }
    });

    return {
      confirming,
      hasError,
      isStoreCreation,
    };
  },
});
</script>
